import { useState } from "react";
import { Link } from "react-router-dom";
import { ListBullets, ShieldCheck, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AppHeader } from "@/components/observer/AppHeader";
import { api, errMsg } from "@/lib/api";
import { useUser } from "@/lib/user";
import { btnGhost, btnPrimary, inputCls, labelCls } from "@/lib/ui";

const Card = ({ title, hint, children }) => (
  <div className="border border-[var(--o-border)]">
    <div className="px-5 py-4 border-b border-[var(--o-border)]">
      <h2 className="font-display text-base md:text-lg font-bold">{title}</h2>
      {hint && <p className="text-xs text-[var(--o-muted2)] mt-1">{hint}</p>}
    </div>
    <div className="p-5 space-y-4">{children}</div>
  </div>
);

export default function Account() {
  const user = useUser();
  const [name, setName] = useState(user.name);
  const [pw, setPw] = useState({ current: "", next: "", again: "" });
  const [busy, setBusy] = useState("");

  const saveName = async (e) => {
    e.preventDefault();
    setBusy("name");
    try {
      const { data } = await api.put("/account", { name });
      user.setUser(data.user);
      toast.success("Name saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.again) return toast.error("The new passwords don't match");
    setBusy("pw");
    try {
      await api.post("/account/password", { current: pw.current, new: pw.next });
      setPw({ current: "", next: "", again: "" });
      toast.success("Password changed. Other devices have been signed out.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const signOutEverywhere = async () => {
    setBusy("devices");
    try {
      await api.post("/account/signout-all");
      toast.success("Every other device is signed out. This one stays.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="noise min-h-screen bg-[var(--o-bg)] text-[var(--o-fg)]" data-testid="account-page">
      <AppHeader />
      <main className="relative z-10 max-w-3xl px-4 sm:px-10 pb-24">
        <div className="pt-12 pb-8 rise">
          <p className={labelCls}>Your account · {user.role === "admin" ? "Admin" : "Member"}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">{user.name}</h1>
          <p className="text-sm text-[var(--o-muted)] mt-2 font-mono">{user.email}</p>
        </div>
        <div className="space-y-6">
          <Card title="Name" hint="Shown to admins and on meetings you run.">
            <form onSubmit={saveName} className="flex flex-col sm:flex-row gap-3">
              <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" data-testid="account-name" />
              <button className={btnPrimary} disabled={busy === "name" || !name.trim() || name === user.name} data-testid="account-name-save">Save</button>
            </form>
          </Card>
          <Card title="Password" hint="At least 10 characters. Changing it signs you out on every other device.">
            <form onSubmit={savePassword} className="grid sm:grid-cols-3 gap-3 items-end">
              <input type="email" value={user.email} autoComplete="username" readOnly hidden />
              {[["current", "Current", "current-password"], ["next", "New", "new-password"], ["again", "New, again", "new-password"]].map(([k, l, ac]) => (
                <label key={k} className="space-y-1.5 block">
                  <span className={labelCls}>{l}</span>
                  <input type="password" className={inputCls} value={pw[k]} onChange={(e) => setPw({ ...pw, [k]: e.target.value })} autoComplete={ac} data-testid={`account-pw-${k}`} />
                </label>
              ))}
              <div className="sm:col-span-3">
                <button className={btnPrimary} disabled={busy === "pw" || !pw.current || !pw.next || !pw.again} data-testid="account-pw-save">Change password</button>
              </div>
            </form>
          </Card>
          <Card title="Devices" hint="Lost a laptop, or signed in on a shared machine? End every session except this one.">
            <button type="button" onClick={signOutEverywhere} disabled={busy === "devices"} className={btnGhost} data-testid="account-signout-all"><SignOut size={15} /> Sign out everywhere else</button>
          </Card>
          <Card title="Your data" hint="Your meetings are files on this server that only you and admins can open. The way you like to start a meeting (provider, chime, flag interval, in the room or online) is kept on your account.">
            <div className="flex flex-wrap gap-3">
              <Link to="/meetings" className={btnGhost} data-testid="account-meetings-link"><ListBullets size={15} /> Your meetings · download or delete</Link>
              <Link to="/privacy" className={btnGhost} data-testid="account-privacy-link"><ShieldCheck size={15} /> What it hears and keeps</Link>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
